import type { ReactNode } from 'react'

import type { GameMeta } from '@hazel/shared'

import { Card } from './Card.js'
import { KeyCap } from './KeyCap.js'
import { TitleCard } from './TitleCard.js'

/**
 * The card grid, drawn the same way wherever something is being chosen.
 *
 * There are two screens that ask "which game?" — the picker at `/` and the
 * room's chooser on the television — and they had answered in two different
 * languages: a grid of artwork on one, a stack of plain text buttons on the
 * other. Same question, same television, same child.
 *
 * So the face lives here and both screens compose it. Not to save the markup,
 * which is small, but because two copies of a card is how the two screens come
 * to disagree — this app has already had a television drawing a board by a
 * rule of its own (DOL-80), and a legend offering a key that did nothing.
 *
 * What each screen wraps this in stays its own business: the picker uses a
 * `Link`, because opening a game is going somewhere and should behave like it
 * (middle-click, the browser's own back). The room uses a `button`, because
 * starting a round is an action and there is nowhere to go. A shared *face*
 * rather than a shared card is what lets those stay honest.
 */

/**
 * Four across on the television rather than the three a laptop gets.
 *
 * A card is as wide as its column and its artwork is 4:3, so fewer columns
 * means taller cards: three across made each card 608x518 on a 1080 screen,
 * far larger than it needs to be to be recognised from a sofa, and got two and
 * a bit games onto the screen.
 */
export const PICKER_GRID =
  'grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3 [[data-scale=tv]_&]:grid-cols-4'

/**
 * The keycap in the corner, on the card it belongs to.
 *
 * A key hint nobody can find is the same as no key hint. Hidden where the
 * pointer is coarse, since a phone has no key to press — and absent entirely
 * for a card so far down a list that it has run out of keys, because drawing
 * no badge is the honest way to say a card has none.
 */
function Shortcut({ shortcut }: { shortcut: string | null }) {
  if (!shortcut) return null

  return (
    <span
      aria-hidden="true"
      className="absolute top-3 right-3 z-10 [@media(pointer:coarse)]:hidden"
    >
      <KeyCap>{shortcut}</KeyCap>
    </span>
  )
}

const FACE = 'hover:border-primary relative flex h-full flex-col gap-3 transition-colors'

export interface GameCardFaceProps {
  readonly game: GameMeta
  /** The key that opens this one, or null where there is none to offer. */
  readonly shortcut: string | null
  /**
   * The line under the picture.
   *
   * The picker puts the game's blurb here. The room puts what starting it
   * does — which is how "Another word" survives the move to artwork, since a
   * picture cannot say that and the wording still has to be said somewhere.
   */
  readonly caption: string
}

/** A game, as its own artwork with a line underneath. */
export function GameCardFace({ game, shortcut, caption }: GameCardFaceProps) {
  return (
    <Card className={FACE}>
      <Shortcut shortcut={shortcut} />
      <TitleCard game={game} />
      <p className="text-ink-muted text-(length:--text-small)">{caption}</p>
    </Card>
  )
}

export interface PlainCardFaceProps {
  readonly shortcut: string | null
  readonly title: string
  readonly children: ReactNode
}

/**
 * A card for the thing in the grid that is not a game.
 *
 * "Play together" on the picker, and its mirror image in the room: the way
 * back to playing on your own. Centred rather than top-aligned, because there
 * is no picture to sit under and a lone paragraph pinned to the top of a card
 * beside four photographs reads as a card that failed to load.
 */
export function PlainCardFace({ shortcut, title, children }: PlainCardFaceProps) {
  return (
    <Card className={`${FACE} justify-center text-center`}>
      <Shortcut shortcut={shortcut} />
      <p className="font-display text-(length:--text-heading)">{title}</p>
      <p className="text-ink-muted text-(length:--text-small)">{children}</p>
    </Card>
  )
}
